import { SearchType } from '../components/dealerLocatorLanding/types/dealerLocatorLanding.types';
import { windowService } from './window.utils';

function googleDataLayerPush(dataLayerData: any) {
  windowService().dataLayer = windowService()?.dataLayer || [];

  if (!windowService().dataLayer) {
    return;
  }

  windowService().dataLayer.push(dataLayerData);
}

function generateGoogleLinkUrl(url: string | undefined): string {
  if ((url || '').startsWith('/')) {
    return windowService()?.location?.origin + url;
  }
  return url || '';
}

function googleNavClickEvent(type: string, link_url: string | undefined, link_text: string | undefined): void {
  const dataLayerData = {
    event: 'navigation_click',
    type,
    link_url: generateGoogleLinkUrl(link_url),
    link_text,
  };

  googleDataLayerPush(dataLayerData);
}

function googleMarketLangClickEvent(type: string, current_market_value: string | undefined, current_lang_value: string | undefined): void {
  const dataLayerData = {
    event: 'market_lang_click',
    type,
    current_market_value: current_market_value,
    current_lang_value: current_lang_value,
  };

  googleDataLayerPush(dataLayerData);
}

function googleSocialProfileClickEvent(platform_link: string | undefined, platform_name: string | undefined) {
  const dataLayerData = {
    event: 'social_profile_click',
    platform_link: generateGoogleLinkUrl(platform_link),
    platform_name,
  };

  googleDataLayerPush(dataLayerData);
}

function googleSocialShareClickEvent(shared_link: string | undefined, sharing_platform: string | undefined) {
  const dataLayerData = {
    event: 'social_share_click',
    _clear: true,
    'data-tag-meta': {
      shared_link: generateGoogleLinkUrl(shared_link),
      sharing_platform,
    },
  };

  googleDataLayerPush(dataLayerData);
}

function googleCtaClickEvent(type: string, link_url: string | undefined, link_text: string | undefined, popup_content: string | undefined) {
  const dataLayerData = {
    event: 'cta_click',
    type,
    link_url: generateGoogleLinkUrl(link_url),
    link_text,
    popup_content,
  };
  googleDataLayerPush(dataLayerData);
}

function googleExportEvent(
  event: string,
  export_type: string | undefined,
  product_line: string | undefined,
  product_series: string | undefined
): void {
  const dataLayerData = {
    event,
    export_type,
    product_line,
    product_series,
  };

  googleDataLayerPush(dataLayerData);
}

function googleFileDownloadClickEvent(
  file_name: string | undefined,
  content_type: string | undefined,
  link_text: string | undefined,
  link_url: string | undefined
): void {
  const dataLayerData = {
    event: 'file_download',
    file_name,
    content_type,
    link_text,
    link_url: generateGoogleLinkUrl(link_url),
  };

  googleDataLayerPush(dataLayerData);
}

function googleHomeCommunityClickEvent(title: string | undefined, link_url: string | undefined, link_text: string | undefined): void {
  const dataLayerData = {
    event: 'home_community_click',
    title,
    link_url: generateGoogleLinkUrl(link_url),
    link_text,
  };

  googleDataLayerPush(dataLayerData);
}

function googleHomeServicesClickEvent(title: string | undefined, link_url: string | undefined, link_text: string | undefined): void {
  const dataLayerData = {
    event: 'home_services_click',
    title,
    link_url: generateGoogleLinkUrl(link_url),
    link_text,
  };

  googleDataLayerPush(dataLayerData);
}

function googleLatestUpdatesClickEvent(title: string | undefined, link_url: string | undefined, link_text: string | undefined): void {
  const dataLayerData = {
    event: 'home_latest_updates_click',
    title,
    link_url: generateGoogleLinkUrl(link_url),
    link_text,
  };

  googleDataLayerPush(dataLayerData);
}

function googleNextStepsClick(
  event: string,
  title: string,
  linkText: string,
  linkUrl: string,
  productLine: string,
  productSeries: string,
  productModel: string
) {
  const dataLayerData = {
    event,
    title,
    product_line: productLine,
    product_series: productSeries,
    link_text: linkText,
    link_url: generateGoogleLinkUrl(linkUrl),
  };

  if (productModel) {
    dataLayerData['product_model'] = productModel;
  }

  googleDataLayerPush(dataLayerData);
}

function googleTabButtonClickEvent(
  event: string,
  title: string | undefined,
  product_line: string | undefined,
  product_series: string | undefined,
  product_model: string | undefined
): void {
  const dataLayerData = {
    event,
    title,
    product_line,
    product_series,
  };

  if (product_model) {
    dataLayerData['product_model'] = product_model;
  }
  googleDataLayerPush(dataLayerData);
}

function googleMediaClickEvent(
  event: string | undefined,
  title: string | undefined,
  media_type: string | undefined,
  product_line: string | undefined,
  product_series: string | undefined
): void {
  const dataLayerData = {
    event,
    title,
    media_type,
    product_line,
    product_series,
  };

  googleDataLayerPush(dataLayerData);
}

function googleButtonClickEvent(
  event: string,
  title: string | undefined,
  type: string | undefined,
  product_line: string | undefined,
  product_series: string | undefined,
  product_model = ''
): void {
  const dataLayerData = {
    event,
    title,
    type,
    product_line,
    product_series,
  };

  if (product_model) {
    dataLayerData['product_model'] = product_model;
  }

  googleDataLayerPush(dataLayerData);
}

function googleModelsCtaClick(
  type: string | undefined,
  product_line: string | undefined,
  product_series: string | undefined,
  linkText: string | undefined,
  linkUrl: string | undefined
): void {
  const dataLayerData = {
    event: 'series_models_cta_click',
    type,
    product_line,
    product_series,
    link_text: linkText,
    link_url: generateGoogleLinkUrl(linkUrl),
  };

  googleDataLayerPush(dataLayerData);
}

function googleSpecialOffersClick(
  event: string | undefined,
  title: string | undefined,
  product_line: string | undefined,
  product_series: string | undefined,
  linkText: string | undefined,
  linkUrl: string | undefined
): void {
  const dataLayerData = {
    event,
    title,
    product_line,
    product_series,
    link_text: linkText,
    link_url: generateGoogleLinkUrl(linkUrl),
  };
  googleDataLayerPush(dataLayerData);
}
function googleLineSeriesClick(
  event: string,
  product_line: string | undefined,
  product_series: string | undefined,
  linkText: string | undefined,
  linkUrl: string | undefined
) {
  const dataLayerData = {
    event,
    product_line,
    link_text: linkText,
    link_url: generateGoogleLinkUrl(linkUrl),
  };

  if (product_series) {
    dataLayerData['product_series'] = product_series;
  }

  googleDataLayerPush(dataLayerData);
}

function googleBrochuresDownload(
  event: string,
  title: string | undefined,
  product_line: string | undefined,
  product_series: string | undefined,
  linkUrl: string | undefined
): void {
  const dataLayerData = {
    event,
    title,
    product_line,
    product_series,
    link_url: generateGoogleLinkUrl(linkUrl),
  };

  googleDataLayerPush(dataLayerData);
}

function googleMediaPlay(event: string, title: string | undefined, productLine: string | undefined) {
  const dataLayerData = {
    event,
    title,
    product_line: productLine,
  };

  googleDataLayerPush(dataLayerData);
}

function googleFormSubmitEvent(form_name: string | undefined): void {
  const dataLayerData = {
    event: 'form_submit',
    form_name,
  };

  googleDataLayerPush(dataLayerData);
}

function googleModelSpecificationsCtaClick(event, product_line, product_series, product_model, title, export_type) {
  const dataLayerData = {
    event,
    product_line,
    product_series,
    product_model,
  };
  if (title) {
    dataLayerData['title'] = title;
  }

  if (export_type) {
    dataLayerData['export_type'] = export_type;
  }

  googleDataLayerPush(dataLayerData);
}

function googleClickDealerLocatorResults(
  event: string,
  dealerLocatorCountry: string | undefined,
  dealerLocatorSearch: string | undefined,
  dealerLocatorEquipment: string | undefined,
  dealerLocatorServices: string | undefined,
  dealerLocatorState: string | undefined
): void {
  const dataLayerData = {
    event,
    dealerLocatorCountry,
    dealerLocatorSearch,
    dealerLocatorEquipment,
    dealerLocatorServices,
    dealerLocatorState,
  };

  googleDataLayerPush(dataLayerData);
}

function googleGeolocationClick(event: string, region: string | undefined, language: string | undefined, market: string | undefined): void {
  const dataLayerData = {
    event,
    region,
    language,
    market,
  };

  googleDataLayerPush(dataLayerData);
}

function getPreselectedMarketInDealerLocator(sitecoreContext): string | undefined {
  const componentList = sitecoreContext?.route.placeholders['jss-main'];
  const dealerLocatorLandingComponent = componentList?.find((item) => item.componentName === 'DealerLocatorLanding');
  const dealerLocatorSettings = dealerLocatorLandingComponent?.fields?.settings;

  if (dealerLocatorSettings?.dealerLocatorSearchType !== SearchType.SearchByLocationOrDealer) {
    return;
  }

  const countryCode = dealerLocatorSettings?.dealerLocatorDefaultCountry;
  const country = dealerLocatorSettings?.dealerLocatorCountryState?.find((country) => country.IsoCode === countryCode);
  const countryName = country.Country;

  return countryName ?? countryCode;
}

function googleClickDealerLocatorGetDetails(
  event: string,
  dealerLocatorCountry: string | undefined,
  dealerLocatorSearch: string | undefined,
  dealerName: string | undefined,
  certifiedDealer: string | undefined
): void {
  const dataLayerData = {
    event,
    dealerLocatorCountry,
    dealerLocatorSearch,
    dealerName,
    certifiedDealer,
  };
  googleDataLayerPush(dataLayerData);
}
function googleDealerLocatorContactClick(event: string, type: string | undefined, dealerName: string | undefined): void {
  const dataLayerData = {
    event,
    type,
    dealerName,
  };
  googleDataLayerPush(dataLayerData);
}

export {
  googleDataLayerPush,
  googleNavClickEvent,
  googleMarketLangClickEvent,
  googleSocialProfileClickEvent,
  googleSocialShareClickEvent,
  googleCtaClickEvent,
  googleHomeCommunityClickEvent,
  googleFileDownloadClickEvent,
  googleExportEvent,
  googleHomeServicesClickEvent,
  googleLatestUpdatesClickEvent,
  googleNextStepsClick,
  googleTabButtonClickEvent,
  googleMediaClickEvent,
  googleButtonClickEvent,
  googleModelsCtaClick,
  googleModelSpecificationsCtaClick,
  googleSpecialOffersClick,
  googleLineSeriesClick,
  googleBrochuresDownload,
  googleMediaPlay,
  googleFormSubmitEvent,
  googleClickDealerLocatorResults,
  googleGeolocationClick,
  getPreselectedMarketInDealerLocator,
  googleClickDealerLocatorGetDetails,
  googleDealerLocatorContactClick,
};
